/**
 * <AuthSplash> — the pre-router loading surface.
 *
 * Rendered by `createAppRoot` during the `await refreshOnLoad()` window
 * (~50–200ms on cold start with a session cookie). Shape-matches AuthLayout
 * so the transition to the real login card is visually seamless — same
 * viewport bg, same card width, same shadow. Only the content differs.
 *
 * Per UI-SPEC §3 "Auth Loading Splash":
 *   - Full-viewport `bg-surface`
 *   - Centered card skeleton (same 400px max-width as the real card)
 *   - `aria-label="Loading"` on the status region
 *   - `prefers-reduced-motion` collapses the pulse to a solid fill
 *     (Skeleton primitive already handles this via `motion-reduce:animate-none`).
 */
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Skeleton, Spinner, nonEmpty } from "@linkhr/design-system";

/** Failed attempts in a row before the splash offers a way out of the wait. */
export const SPLASH_WAY_OUT_AFTER = 4;

export interface AuthSplashProps {
  /** An attempt got no answer (or a 5xx) and the restore will try again. */
  unreachable?: boolean;
  /** Such attempts in a row. */
  failures?: number;
  /** End the current wait and try at once. */
  onRetryNow?: () => void;
  /** Skip the restore and go to the login form. */
  onSignInAgain?: () => void;
}

export function AuthSplash({
  unreachable = false,
  failures = 0,
  onRetryNow,
  onSignInAgain,
}: AuthSplashProps): JSX.Element {
  const { t } = useTranslation("auth");
  // `t` returns `string | object` — branded NonEmptyString consumers need
  // the narrowed string. `nonEmpty` enforces non-empty at runtime too.
  const loadingLabel = nonEmpty(t("loading.aria", { defaultValue: "Loading" }));
  // The session restore got no answer (or a 5xx) and is about to try again.
  // `common` is an eager namespace, so this renders before anything lazy loads.
  const unreachableText = unreachable ? t("common:network.unreachable") : null;
  // The restore keeps trying for as long as it takes; after a few failures the
  // person can stop waiting. Retries go on in the background until they choose.
  const wayOut = failures >= SPLASH_WAY_OUT_AFTER && onRetryNow !== undefined;

  return (
    <div
      className="bg-surface flex min-h-screen items-center justify-center p-xl"
      role="status"
      aria-live="polite"
    >
      <Card className="w-full max-w-[400px]">
        <div className="flex flex-col items-center gap-md">
          <Spinner aria-label={loadingLabel} />
          {unreachableText !== null ? (
            <p className="text-center text-body text-muted">{unreachableText}</p>
          ) : null}
          {wayOut ? (
            <div className="flex flex-wrap justify-center gap-sm">
              <Button variant="primary" onClick={onRetryNow}>
                {t("common:network.try_again_now")}
              </Button>
              {onSignInAgain !== undefined ? (
                <Button variant="secondary" onClick={onSignInAgain}>
                  {t("common:network.sign_in_again")}
                </Button>
              ) : null}
            </div>
          ) : null}
          <Skeleton className="w-full" height={32} aria-label={loadingLabel} />
          <Skeleton className="w-full" height={40} aria-label={loadingLabel} />
          <Skeleton className="w-full" height={40} aria-label={loadingLabel} />
        </div>
      </Card>
    </div>
  );
}
